import React, { memo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useSelector } from 'react-redux';

import palette from '../../components/styles/palette';
import layout from '../../components/styles/layout';
import { Text } from '../../components/Base';
import { getActiveTabIndex } from '../../utils/query';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import {
  DEPOT_SUMMARY,
  SECTION_SUMMARY,
  REQUEST,
  WARNING,
} from '../../constants/strings';
import {
  subHeaderConfig,
  subHeaderIconsConfig,
} from '../../enums/subHeaderConfig';
import { RequestIcon, SubHeaderWarningIcon } from '../../components/icons';
import { getSectionName } from '../../store/selectors/sections';
import { getDepotStatsObject } from '../../store/selectors/depot';

const useStyles = makeStyles(({ typography }) => {
  const { pxToRem } = typography;
  return {
    subheader: {
      backgroundColor: palette.slate,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      height: pxToRem(layout.subheader.height),
      padding: `0 ${pxToRem(70)} 0 ${pxToRem(20)}`,
      color: palette.typography.white.solid,
    },
    text: {
      fontSize: pxToRem(12),
      fontWeight: 500,
      lineHeight: pxToRem(16),
    },
    container: {
      display: 'flex',
      height: pxToRem(16),
    },
    title: {
      color: palette.typography.white.solid,
    },
    label: {
      color: palette.typography.white.high,
    },
    value: {
      fontWeight: 'normal',
    },
    requestIcon: {
      width: pxToRem(16),
      height: pxToRem(16),
      marginRight: pxToRem(4.8),
    },
  };
});

// eslint-disable-next-line react/display-name
const StatItemLabel = memo(({ label, dataTestId }) => {
  const styles = useStyles();
  return (
    <Text
      className={clsx(styles.text, styles.label)}
      data-testid={`${dataTestId}-${label}`}
    >
      {label}
    </Text>
  );
});

// eslint-disable-next-line react/display-name
const StatIconLabel = memo(({ label, dataTestId }) => {
  const styles = useStyles();

  switch (label) {
    case REQUEST:
      return (
        <RequestIcon className={styles.requestIcon} data-testid={dataTestId} />
      );
    case WARNING:
      return (
        <SubHeaderWarningIcon
          className={styles.requestIcon}
          data-testid={dataTestId}
        />
      );
    default:
      return null;
  }
});

// eslint-disable-next-line react/display-name
const StatItemValue = memo(({ value, dataTestId }) => {
  const styles = useStyles();

  return (
    <Text
      className={clsx(styles.text, styles.label, styles.value)}
      data-testid={dataTestId}
    >
      {value}
    </Text>
  );
});

// eslint-disable-next-line react/display-name
const StatItem = memo(({ children }) => {
  const styles = useStyles();

  return <Box className={styles.container}>{children}</Box>;
});

const SubHeader = () => {
  const { pathname } = useLocation();
  const { sectionId } = useLocationQueryParams();
  const styles = useStyles();
  const activeTabIndex = getActiveTabIndex(pathname);
  const sectionName = useSelector((state) => getSectionName(state, sectionId));
  const depotStats = useSelector((state) =>
    getDepotStatsObject(state, !!activeTabIndex)
  );

  return (
    <Box className={styles.subheader} data-testid="subheader">
      <Text className={clsx(styles.text, styles.title)}>
        {!activeTabIndex || !sectionId
          ? DEPOT_SUMMARY
          : `${SECTION_SUMMARY}: ${sectionName}`}
      </Text>
      {subHeaderConfig.map(({ key, subKey, label, formatValue }) => (
        <StatItem key={key}>
          <StatItemLabel label={label} dataTestId={`${key}-${label}`} />
          &nbsp;
          <StatItemValue
            value={formatValue(depotStats[key], depotStats[subKey])}
            dataTestId={`${key}-value`}
          />
        </StatItem>
      ))}
      {subHeaderIconsConfig.map(({ key, subKey, label, formatValue }) => {
        const value = formatValue(depotStats[key], depotStats[subKey]);
        return (
          !!value && (
            <StatItem key={key}>
              <StatIconLabel label={label} dataTestId={`${key}-${label}`} />
              &nbsp;
              <StatItemValue value={value} dataTestId={`${key}-value`} />
            </StatItem>
          )
        );
      })}
    </Box>
  );
};

export default memo(SubHeader);
